/**
 * `<caos-ask-box>` — the question somebody asked, the QUERY it became, and the rows that query returned.
 * (Catalog: AI capabilities › AskBox — the Ask capability's binding: an editable query spec above the result list it produced.)
 *
 * CAOS-158. Ask is one of the five capabilities the platform ships, and this is the piece that
 * places it on a screen. The whole design of Ask is that it does NOT answer the question: it
 * translates the question into a query spec, the kernel runs that spec under the asker's own access,
 * and the result set is the answer. So the binding has to show BOTH halves, and it has to show them
 * in that order, or it reads as a model that knows things about your data.
 *
 * ── WHY THE SPEC IS EDITABLE, AND NOT MERELY DISPLAYED ───────────────────────────────────────────
 * The model's contribution is the translation, which is checkable; the data is the platform's, which
 * is authoritative. A person who disagrees with the answer is nearly always disagreeing with the
 * QUESTION — the wrong object, a filter that should not be there, a field they wanted and did not
 * get. Given a read-only spec their only move is to rephrase the sentence and hope, which is the
 * interaction every assistant text box has and the reason none of them is trusted twice. Given an
 * editable one they fix the query and re-run it, and what they fixed is legible to the next person.
 *
 * So editing the spec does not re-translate. It raises `caos-ask-run` carrying the edited spec, and
 * the application runs THAT — the model is not consulted again, because it has already done the only
 * thing it was asked to do, and re-translating would throw away the correction somebody just made.
 * Asking a NEW question is the application's own affair: this piece takes the question as
 * `utterance` and never collects one, because the place a question is typed differs per surface —
 * a global bar, a panel, a field on a page — and a component that owned one would fix that choice.
 *
 * ── THE SPEC STAYS ON SCREEN WHEN THE RESULT IS EMPTY ────────────────────────────────────────────
 * Zero rows is an answer about the query, not an absence of an answer, and it is the case where
 * seeing the query matters most: "no records match `stage = 'closed'`" is information, and "nothing
 * found" is not. A binding that hid the spec until there were rows would withhold the explanation
 * exactly when it was needed, so the spec is drawn whenever there is one.
 *
 * ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────────────────────────────
 * It does not invoke the capability, run the query, or save the list view. There is no transport in
 * a component library, and a piece that both composed the request and sent it would be unusable by
 * any application whose kernel sits somewhere else. It raises events; the application acts.
 *
 * It also does not validate the spec against the org's shape. Whether `account` is an object and
 * whether this person may read it are facts the kernel holds and enforces on the run — re-deciding
 * them here would be a second, weaker copy of the access rule, and the weaker copy is the one that
 * gets believed. An object or field the org does not have comes back as a refusal from the run,
 * which is where it belongs.
 *
 * Styling seams: `::part(ask)`, `::part(utterance)`, `::part(spec)`, `::part(spec-row)`,
 * `::part(spec-label)`, `::part(spec-input)`, `::part(spec-actions)`, `::part(run)`, `::part(save)`,
 * `::part(results)`, `::part(empty)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import './result-grid.js';
import type { CaosResultGrid, CaosResultColumn, CaosResultRow } from './result-grid.js';

/**
 * A query spec, in exactly the shape the shipped `ai_ask` prompt declares as its output contract
 * (`kernel/src/ai/standard-capabilities.ts`): object, fields, an optional predicate, an optional
 * limit. Written out here rather than imported because `@caos/ui` does not depend on the kernel
 * package — the same reason `ComponentInputType` is duplicated in `component-docs.ts`. Keep the
 * members identical to that schema; a spec this piece could edit into a shape the contract refuses
 * would be a spec the run rejects after the person pressed Run.
 */
export interface CaosAskSpec {
  /** The object being queried. */
  readonly object: string;
  /** The fields to return, in the order they should be shown. */
  readonly fields: readonly string[];
  /** The predicate, in the platform's own expression language. Absent ⇒ every record the asker may read. */
  readonly where?: string;
  /** How many records at most. Absent ⇒ the kernel's own default. */
  readonly limit?: number;
}

/** The four editable parts of a spec, in the order they are drawn. */
const FIELD_ORDER = ['object', 'fields', 'where', 'limit'] as const;

type SpecField = (typeof FIELD_ORDER)[number];

const LABELS: Readonly<Record<SpecField, string>> = {
  object: 'Object',
  fields: 'Fields',
  where: 'Where',
  limit: 'Limit',
};

const PLACEHOLDERS: Readonly<Record<SpecField, string>> = {
  object: 'which object',
  fields: 'comma separated',
  where: 'every record you may read',
  limit: 'the default',
};

const sheet = makeSheet(`
  :host { display: block; }

  .ask { display: flex; flex-direction: column; gap: var(--caos-space-3); }

  .utterance {
    margin: 0;
    font-family: var(--caos-font);
    font-size: 14px;
    line-height: 1.5;
    color: var(--caos-color-text);
  }
  .utterance:empty { display: none; }
  .utterance .said {
    display: block;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
  }

  .spec {
    display: flex;
    flex-direction: column;
    gap: var(--caos-space-2);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius);
    background: var(--caos-color-surface-2);
    padding: var(--caos-space-3);
  }

  .spec-row { display: flex; align-items: center; gap: var(--caos-space-2); }

  .spec-label {
    flex: none;
    width: 68px;
    font-family: var(--caos-font);
    font-size: 12px;
    font-weight: 600;
    color: var(--caos-color-text-muted);
  }

  .spec-input {
    flex: 1 1 auto;
    min-width: 0;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    font-family: var(--caos-font-mono);
    font-size: 12.5px;
    padding: 5px 8px;
  }
  .spec-input:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .spec-input:disabled { opacity: var(--caos-state-disabled-opacity); }

  .spec-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--caos-space-2);
  }

  .edited {
    margin-right: auto;
    font-family: var(--caos-font);
    font-size: 12px;
    color: var(--caos-color-accent);
  }

  button {
    font-family: var(--caos-font);
    font-size: 12.5px;
    border-radius: var(--caos-radius-sm);
    padding: 5px 12px;
    cursor: pointer;
  }
  button:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  button:disabled { opacity: var(--caos-state-disabled-opacity); cursor: not-allowed; }

  .run {
    border: 1px solid var(--caos-color-accent);
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    font-weight: 600;
  }
  .run:hover:not(:disabled) { background: var(--caos-color-accent-fill-hover); }
  .run:disabled { cursor: progress; }

  .save {
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface);
    color: var(--caos-color-text-muted);
  }
  .save:hover:not(:disabled) { color: var(--caos-color-text); border-color: var(--caos-color-border-strong); }

  .empty {
    margin: 0;
    font-family: var(--caos-font);
    font-size: 13px;
    color: var(--caos-color-text-muted);
  }
`);

export class CaosAskBox extends CaosElement {
  static get observedAttributes(): string[] {
    return ['utterance', 'running', 'run-label', 'save-label', 'empty-text', 'readonly'];
  }

  private utteranceEl: HTMLParagraphElement;
  private specEl: HTMLDivElement;
  private editedEl: HTMLSpanElement;
  private actionsEl: HTMLDivElement;
  private runEl: HTMLButtonElement;
  private saveEl: HTMLButtonElement;
  private gridEl: CaosResultGrid;
  private emptyEl: HTMLParagraphElement;

  private readonly inputs = new Map<SpecField, HTMLInputElement>();

  /** The spec as it arrived, so "edited" is a comparison rather than a flag somebody sets. */
  private pristine: CaosAskSpec | null = null;
  /** The spec as it is on screen. */
  private live: CaosAskSpec | null = null;

  private cols: readonly CaosResultColumn[] = [];
  private data: readonly CaosResultRow[] = [];
  /** Whether a result was supplied at all, which is not the same as one that came back empty. */
  private answered = false;

  constructor() {
    super(sheet);

    const wrap = document.createElement('div');
    wrap.className = 'ask';
    wrap.setAttribute('part', 'ask');

    this.utteranceEl = document.createElement('p');
    this.utteranceEl.className = 'utterance';
    this.utteranceEl.setAttribute('part', 'utterance');

    this.specEl = document.createElement('div');
    this.specEl.className = 'spec';
    this.specEl.setAttribute('part', 'spec');
    this.specEl.setAttribute('role', 'group');
    this.specEl.setAttribute('aria-label', 'The query this question became');

    for (const field of FIELD_ORDER) {
      const row = document.createElement('div');
      row.className = 'spec-row';
      row.setAttribute('part', 'spec-row');

      const label = document.createElement('label');
      label.className = 'spec-label';
      label.setAttribute('part', 'spec-label');
      label.textContent = LABELS[field];
      label.htmlFor = `ask-${field}`;

      const input = document.createElement('input');
      input.id = `ask-${field}`;
      input.className = 'spec-input';
      input.setAttribute('part', 'spec-input');
      input.type = field === 'limit' ? 'number' : 'text';
      input.placeholder = PLACEHOLDERS[field];
      input.addEventListener('input', () => this.onEdit());
      this.inputs.set(field, input);

      row.append(label, input);
      this.specEl.append(row);
    }

    this.actionsEl = document.createElement('div');
    this.actionsEl.className = 'spec-actions';
    this.actionsEl.setAttribute('part', 'spec-actions');

    this.editedEl = document.createElement('span');
    this.editedEl.className = 'edited';

    this.saveEl = document.createElement('button');
    this.saveEl.type = 'button';
    this.saveEl.className = 'save';
    this.saveEl.setAttribute('part', 'save');
    this.saveEl.addEventListener('click', () => this.emit('caos-ask-save-view'));

    this.runEl = document.createElement('button');
    this.runEl.type = 'button';
    this.runEl.className = 'run';
    this.runEl.setAttribute('part', 'run');
    this.runEl.addEventListener('click', () => this.run());

    this.actionsEl.append(this.editedEl, this.saveEl, this.runEl);
    this.specEl.append(this.actionsEl);

    this.gridEl = document.createElement('caos-result-grid');
    this.gridEl.setAttribute('part', 'results');

    this.emptyEl = document.createElement('p');
    this.emptyEl.className = 'empty';
    this.emptyEl.setAttribute('part', 'empty');
    this.emptyEl.setAttribute('role', 'status');

    wrap.append(this.utteranceEl, this.specEl, this.gridEl, this.emptyEl);
    this.root.append(wrap);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  /** The query spec as it is on screen — the edited one, which is what Run would send. */
  get spec(): CaosAskSpec | null {
    return this.live;
  }

  set spec(next: CaosAskSpec | null) {
    const normalised = normalise(next);
    this.pristine = normalised;
    this.live = normalised;
    this.writeInputs();
    this.render();
  }

  /** True when what is on screen differs from the spec that arrived. */
  get edited(): boolean {
    return stable(this.live) !== stable(this.pristine);
  }

  get columns(): readonly CaosResultColumn[] {
    return this.cols;
  }

  set columns(next: readonly CaosResultColumn[]) {
    this.cols = Array.isArray(next) ? next : [];
    this.gridEl.columns = this.cols;
    this.render();
  }

  get rows(): readonly CaosResultRow[] {
    return this.data;
  }

  set rows(next: readonly CaosResultRow[]) {
    this.data = Array.isArray(next) ? next : [];
    this.answered = true;
    this.gridEl.rows = this.data;
    this.render();
  }

  private get running(): boolean {
    return this.hasAttribute('running');
  }

  private get readOnly(): boolean {
    return this.hasAttribute('readonly');
  }

  /**
   * Read the four inputs back into a spec.
   *
   * `fields` is a comma-separated list on screen and an array in the contract, and the split drops
   * empty segments — a trailing comma is a person still typing, not a field with no name. `limit` is
   * dropped rather than zeroed when the box is empty: absent means "the kernel's default" and `0`
   * would mean "return nothing", and silently turning one into the other would answer a question
   * nobody asked with an empty list.
   */
  private readInputs(): CaosAskSpec {
    const text = (field: SpecField): string => (this.inputs.get(field)?.value ?? '').trim();
    const limitText = text('limit');
    const limit = limitText === '' ? undefined : Number(limitText);
    const where = text('where');
    return {
      object: text('object'),
      fields: text('fields')
        .split(',')
        .map((f) => f.trim())
        .filter((f) => f !== ''),
      ...(where === '' ? {} : { where }),
      ...(limit === undefined || !Number.isFinite(limit) ? {} : { limit }),
    };
  }

  private writeInputs(): void {
    const spec = this.live;
    const put = (field: SpecField, value: string): void => {
      const input = this.inputs.get(field);
      // Only when it differs: writing the same string back moves the caret to the end, which on an
      // `input` listener means a person cannot type in the middle of what they already wrote.
      if (input && input.value !== value) input.value = value;
    };
    put('object', spec?.object ?? '');
    put('fields', (spec?.fields ?? []).join(', '));
    put('where', spec?.where ?? '');
    put('limit', spec?.limit === undefined ? '' : String(spec.limit));
  }

  private onEdit(): void {
    this.live = this.readInputs();
    this.render();
    this.emit('caos-ask-spec-change');
  }

  private run(): void {
    if (this.running || this.live === null) return;
    this.emit('caos-ask-run');
  }

  private emit(type: string): void {
    this.dispatchEvent(
      new CustomEvent(type, {
        bubbles: true,
        composed: true,
        detail: { spec: this.live, edited: this.edited },
      }),
    );
  }

  private render(): void {
    const said = this.getAttribute('utterance');
    if (said === null || said.trim() === '') {
      this.utteranceEl.replaceChildren();
    } else {
      const caption = document.createElement('span');
      caption.className = 'said';
      caption.textContent = 'Asked';
      this.utteranceEl.replaceChildren(caption, document.createTextNode(said));
    }

    const hasSpec = this.live !== null;
    this.specEl.hidden = !hasSpec;
    for (const input of this.inputs.values()) input.disabled = this.readOnly || this.running;

    this.runEl.textContent = this.running ? 'Running…' : (this.getAttribute('run-label') ?? 'Run');
    this.runEl.disabled = this.running || !hasSpec || this.readOnly;
    this.saveEl.textContent = this.getAttribute('save-label') ?? 'Save as list view';
    this.saveEl.disabled = !hasSpec;
    // Said in words, not only in the accent colour on the Run button — "this is no longer the query
    // the question produced" is the one thing a reader of somebody else's screen needs to know.
    this.editedEl.textContent = this.edited ? 'Edited — Run uses what is on screen' : '';

    // A result that came back with no rows keeps the grid OFF and says so, because an empty grid
    // with headers reads as a surface that has not loaded yet.
    const showGrid = this.answered && this.data.length > 0;
    this.gridEl.hidden = !showGrid;
    this.emptyEl.hidden = !(this.answered && this.data.length === 0);
    this.emptyEl.textContent = this.getAttribute('empty-text') ?? 'No records match this query.';
  }
}

/** Drop a spec that is not one, and freeze the field list so a caller's array cannot change under us. */
function normalise(next: CaosAskSpec | null | undefined): CaosAskSpec | null {
  if (next === null || next === undefined || typeof next !== 'object') return null;
  if (typeof next.object !== 'string') return null;
  return {
    object: next.object,
    fields: Array.isArray(next.fields) ? [...next.fields].map(String) : [],
    ...(typeof next.where === 'string' && next.where !== '' ? { where: next.where } : {}),
    ...(typeof next.limit === 'number' && Number.isFinite(next.limit) ? { limit: next.limit } : {}),
  };
}

/** Compare two specs by what they say, not by identity. */
function stable(spec: CaosAskSpec | null): string {
  if (spec === null) return '';
  return JSON.stringify([spec.object, [...spec.fields], spec.where ?? null, spec.limit ?? null]);
}

define('caos-ask-box', CaosAskBox);

declare global {
  interface HTMLElementTagNameMap {
    'caos-ask-box': CaosAskBox;
  }
}
